import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { base44 } from '@/api/base44Client';
import { getDeviceContext } from '@/lib/deviceContext';
import { getRequiredLoginVersion, signOutForReset, getDeviceLoginVersion, stampLoginVersion, isAwaitingRelogin, clearAwaitingRelogin } from '@/lib/loginVersion';

const POLL_MS = 10000;

// Signs a user out as soon as the admin raises the required login version.
// The account's own stamped version (set at sign-in) is compared, so clearing
// browser storage can't bypass it and a re-signed-in user isn't kicked twice.
export default function LoginVersionGuard() {
  const { user } = useAuth();
  const location = useLocation();
  const stampedRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => { stampedRef.current = user ? (user.login_version || 0) : null; }, [user?.id, user?.login_version]);

  const lastSeenRef = useRef(0);

  const check = async () => {
    if (stampedRef.current === null || doneRef.current) return;
    if (Date.now() - lastSeenRef.current > 5 * 60 * 1000) {
      lastSeenRef.current = Date.now();
      base44.auth.updateMe({ last_seen_at: new Date().toISOString(), last_seen_device: getDeviceContext().label }).catch(() => {});
    }
    const required = await getRequiredLoginVersion().catch(() => null);
    if (!required) return;
    // First check after signing back in (by any route): mark this account and device current.
    if (isAwaitingRelogin()) {
      clearAwaitingRelogin();
      await stampLoginVersion().catch(() => {});
      stampedRef.current = required;
      return;
    }
    if (required && Math.min(stampedRef.current, getDeviceLoginVersion()) < required && !doneRef.current) {
      doneRef.current = true;
      signOutForReset();
    }
  };

  useEffect(() => {
    if (!user) return;
    check();
    const id = setInterval(() => { if (document.visibilityState === 'visible') check(); }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user?.id]);

  useEffect(() => { check(); }, [location.pathname]);

  return null;
}